import { FC, useEffect, useRef, useState } from 'react';

import env from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import { dispatchCookieConsentChange } from '@eneco/hooks/cookieConsent';
import { AlertDialog, Button, SKIP_TO_MAIN_LINK_ID, Stack, TextLink } from '@eneco/sparky';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useContent, useLayoutData } from '@sitecore/common';
import { CookieWallRendering } from '@sitecore/types/CookieWall';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

const COOKIE_VERSIONS = {
  QUBIT: '2.0.0',
  PRIVACY: '1',
} as const;

/** Screen-reader name for this overlay until Sitecore exposes a dedicated field. */
const COOKIE_WALL_SR_LABEL = 'Cookiemelding';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CookieWallLinkReplacement: FC<any> = props => <TextLink {...props} />;

const focusPageStart = (): void => {
  const skipLink = document.getElementById(SKIP_TO_MAIN_LINK_ID);
  if (skipLink instanceof HTMLElement) {
    skipLink.focus();
    return;
  }

  document.getElementById('main')?.focus();
};

const CookieWall: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const acceptButtonRef = useRef<HTMLButtonElement>(null);
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const router = useRouter();
  const { f } = useContent<CookieWallRendering>();
  const { route } = useLayoutData();
  const hideCookieWall = Boolean(route?.fields?.hideCookieWall?.value);

  const handleSetCookie = (accept: boolean) => {
    const qubitValue = accept ? 'Accepted' : 'Declined';
    const privacyValue = accept ? 'true' : 'false';
    const host = env('HOST');
    const domain = host ? new URL(host).hostname.replace(/^www\./, '') : undefined;
    const cookieOptions: Cookies.CookieAttributes = { expires: 365, ...(domain && { domain }) };
    Cookies.set('qubitconsent', qubitValue, cookieOptions);
    Cookies.set('qubitconsent_version', COOKIE_VERSIONS.QUBIT, cookieOptions);
    Cookies.set('privacyConsent', privacyValue, cookieOptions);
    Cookies.set('privacyConsent_type', 'explicit', cookieOptions);
    Cookies.set('privacyConsent_version', COOKIE_VERSIONS.PRIVACY, cookieOptions);
    dispatchCookieConsentChange();
  };

  useEffect(() => {
    const checkCookiesAndAttachListeners = () => {
      const cookiesExists =
        Cookies.get('qubitconsent') && Cookies.get('privacyConsent') && Cookies.get('privacyConsent_type');
      const cookiesVersionMisMatch =
        Cookies.get('qubitconsent_version') !== COOKIE_VERSIONS.QUBIT ||
        Cookies.get('privacyConsent_version') !== COOKIE_VERSIONS.PRIVACY;

      if ((!cookiesExists || cookiesVersionMisMatch) && !hideCookieWall) {
        setIsOpen(true);
      }

      const handleClick = (event: Event) => {
        event.preventDefault();
        lastTriggerRef.current = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
        setIsOpen(true);
      };

      // Find all anchor elements that have '#cookiepermissionmodal' in their href attribute
      const elements = Array.from(document.querySelectorAll('a[href*="#cookiepermissionmodal"]')) as HTMLElement[];
      elements.forEach(element => {
        element.addEventListener('click', handleClick);
      });

      // Cleanup event listener on component unmount
      return () => {
        elements.forEach(element => {
          element.removeEventListener('click', handleClick);
        });
      };
    };

    const cleanup = checkCookiesAndAttachListeners();
    router.events?.on('routeChangeComplete', checkCookiesAndAttachListeners);

    return () => {
      cleanup();
      router.events?.off('routeChangeComplete', checkCookiesAndAttachListeners);
    };
  }, [router]);

  const visibleTitle = f('title.value');

  const footer = (
    <Stack direction={{ initial: 'column', md: 'row' }} gap="5">
      <AlertDialogPrimitive.Action asChild>
        <Button ref={acceptButtonRef} onClick={() => handleSetCookie(true)}>
          {f('yesButtonText.value')}
        </Button>
      </AlertDialogPrimitive.Action>
      <AlertDialogPrimitive.Action asChild>
        <Button onClick={() => handleSetCookie(false)}>{f('noButtonText.value')}</Button>
      </AlertDialogPrimitive.Action>
    </Stack>
  );

  return (
    <AlertDialog
      isOpen={isOpen}
      setOpen={setIsOpen}
      title={visibleTitle}
      accessibleName={`${COOKIE_WALL_SR_LABEL}. ${visibleTitle}`}
      description={
        <RichText
          html={f('description.value')}
          replacements={{
            a: CookieWallLinkReplacement,
          }}
        />
      }
      footer={footer}
      onOpenAutoFocus={event => {
        event.preventDefault();
        acceptButtonRef.current?.focus();
      }}
      onCloseAutoFocus={event => {
        event.preventDefault();
        const trigger = lastTriggerRef.current;
        lastTriggerRef.current = null;
        if (trigger?.isConnected) {
          trigger.focus();
          return;
        }
        focusPageStart();
      }}
    />
  );
};

export default CookieWall;
